import React from 'react';
import { CheckCircle } from 'lucide-react';
import { formatDate } from '../../utils/datetime';

export interface ResolvedTask {
  id: string;
  followup_priority: 'urgent' | 'high' | 'medium' | 'low';
  followup_due_date: string | null;
  followup_notes: string | null;
  followup_materials: string[];
  followup_actions: string[];
  followup_resolved_at: string | null;
  original_visit_date: string;
  created_by_user_name: string;
}

interface VisitResolvedTasksSectionProps {
  tasks: ResolvedTask[];
}

const priorityBadgeColor = (priority: ResolvedTask['followup_priority']) => {
  switch (priority) {
    case 'urgent':
      return 'bg-fuchsia-700 text-white';
    case 'high':
      return 'bg-red-600 text-white';
    case 'medium':
      return 'bg-orange-600 text-white';
    default:
      return 'bg-blue-600 text-white';
  }
};

const Tag: React.FC<{ value: string }> = ({ value }) => (
  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs font-medium">
    {value.replace('_', ' ').toUpperCase()}
  </span>
);

export const VisitResolvedTasksSection: React.FC<VisitResolvedTasksSectionProps> = ({ tasks }) => {
  if (tasks.length === 0) return null;

  return (
    <div className="bg-emerald-50 p-4 rounded-xl border-2 border-emerald-300">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
        <h3 className="font-bold text-emerald-900">
          Closed {tasks.length} open task{tasks.length !== 1 ? 's' : ''}
        </h3>
      </div>
      <div className="space-y-3">
        {tasks.map((task) => (
          <div key={task.id} className="bg-white p-3 rounded-lg border border-emerald-200 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span
                className={`px-2 py-1 rounded-lg text-xs font-bold ${priorityBadgeColor(
                  task.followup_priority
                )}`}
              >
                {task.followup_priority.toUpperCase()}
              </span>
              <span className="text-sm text-gray-600">{formatDate(task.original_visit_date)}</span>
              {task.followup_due_date && (
                <span className="text-sm text-gray-600">
                  Due: {formatDate(task.followup_due_date)}
                </span>
              )}
              <span className="text-sm text-gray-500 truncate">{task.created_by_user_name}</span>
            </div>
            {task.followup_notes && (
              <p className="text-sm text-gray-700 mb-2 break-words">{task.followup_notes}</p>
            )}
            {task.followup_materials.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {task.followup_materials.map((material) => (
                  <Tag key={material} value={material} />
                ))}
              </div>
            )}
            {task.followup_actions.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {task.followup_actions.map((action) => (
                  <Tag key={action} value={action} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
